import React from 'react';
import { Alert, Card, CardHeader, EmptyState, Skeleton } from '@opendate-app/setlist';
import { compactCurrency, currencyFormatter } from '../../utils/reportingUtils';

const SUBTITLE = 'Attributed net revenue';
const count = new Intl.NumberFormat('en-US');
const wholeCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const perSend = (value) => (value == null ? '—' : `${currencyFormatter.format(value)} per send`);

export function impactRows(data, base) {
  const { email, sms, meta, automations } = data;
  return [
    {
      key: 'email', name: 'Email broadcasts', href: `${base}/marketing-email`,
      meta: `${count.format(email.broadcasts)} sent · ${Math.round(email.open_rate)}% open rate`,
      revenue: compactCurrency(email.revenue), efficiency: perSend(email.revenue_per_send),
    },
    {
      key: 'sms', name: 'SMS broadcasts', href: `${base}/marketing-sms`,
      meta: `${count.format(sms.broadcasts)} sent · ${Math.round(sms.click_rate)}% click rate`,
      revenue: compactCurrency(sms.revenue), efficiency: perSend(sms.revenue_per_send),
    },
    meta
      ? {
        key: 'meta', name: 'Meta ads', href: `${base}/marketing/meta-ads`,
        meta: `${wholeCurrency.format(meta.spend)} spent · ${count.format(meta.campaigns)} campaigns`,
        revenue: compactCurrency(meta.revenue), efficiency: meta.roas == null ? '—' : `${meta.roas.toFixed(1)}x ROAS`,
      }
      : { key: 'meta', name: 'Meta ads', href: null, meta: 'Not connected', revenue: '', efficiency: '' },
    {
      key: 'automations', name: 'Automations', href: `${base}/marketing`,
      meta: 'Automated campaigns',
      revenue: compactCurrency(automations.revenue), efficiency: `${count.format(automations.orders)} orders`,
    },
  ];
}

function ImpactRow({ row }) {
  const nameStyle = { color: 'var(--sl-text-primary)' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {row.href ? <a href={row.href} style={nameStyle}>{row.name}</a> : <span style={nameStyle}>{row.name}</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{row.meta}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{row.revenue}</div>
        <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{row.efficiency}</div>
      </div>
    </div>
  );
}

function MarketingImpactCard({ data, loading, error, base, showPurchaseCaption }) {
  const empty = data && !data.meta && [data.email, data.sms, data.automations].every((row) => !row.revenue && !row.sends);

  return (
    <Card
      header={(
        <CardHeader
          title={<a href={`${base}/marketing`} style={{ color: 'inherit' }}>Marketing impact →</a>}
          supportingText={showPurchaseCaption ? `${SUBTITLE} · Sends by send date, revenue by purchase date` : SUBTITLE}
        />
      )}
    >
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} width="100%" height={36} />)}
        </div>
      )}
      {!loading && error && <Alert color="error" title="Couldn't load marketing impact" description="Refresh the page to try again." />}
      {!loading && !error && empty && <EmptyState size="sm" title="No attributed sales in this range" />}
      {!loading && !error && data && !empty && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {impactRows(data, base).map((row) => <ImpactRow key={row.key} row={row} />)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)', marginTop: 16 }}>
            An order can count toward more than one channel. Meta revenue and ROAS are as reported by Meta.
          </div>
        </div>
      )}
    </Card>
  );
}

export default MarketingImpactCard;
